import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { cn } from '@util/index';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';
import { useState } from 'react';
export default function ProductConditions({
  product,
}: {
  product: ProductDocument;
}) {
  const sellerNote = product.specs?.['Seller Notes'] as string | undefined;
  const [isSellerNoteExpanded, setIsSellerNoteExpanded] = useState(false);
  const [hasMoreThanThreeLines, setHasMoreThanThreeLines] = useState(false);

  const measureTextRef = (element: HTMLParagraphElement | null) => {
    if (!element || !sellerNote) return;

    const originalClasses = element.className;
    element.classList.remove('line-clamp-3', 'max-h-[12rem]');
    const fullHeight = element.scrollHeight;
    element.className = originalClasses;

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * 3;
    setHasMoreThanThreeLines(fullHeight > maxHeight);
  };

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-2">
        <Item
          title="Condition"
          value={product.condition}
          sellerNote={sellerNote}
        />

        {sellerNote && (
          <div className="flex flex-col gap-2 rounded-[2rem] bg-zinc-100 p-8">
            <span className="text-[1.6rem] italic text-zinc-500">
              Seller Notes:
            </span>

            <p
              ref={measureTextRef}
              className={cn(
                'text-[1.8rem] italic text-zinc-800',
                !isSellerNoteExpanded && 'line-clamp-3 max-h-[12rem]'
              )}
            >
              {sellerNote}
            </p>

            {hasMoreThanThreeLines && (
              <button
                type="button"
                onClick={() => setIsSellerNoteExpanded(!isSellerNoteExpanded)}
                className="w-fit text-[1.8rem] font-medium text-brand-secondary"
              >
                {isSellerNoteExpanded ? 'Show Less' : 'Read More'}
              </button>
            )}
          </div>
        )}
      </div>

      {['Casual', 'Gear'].some((x) =>
        product.category.toLowerCase().includes(x.toLowerCase())
      ) &&
        !product?.variations &&
        (product.size?.letter || !!product.size?.number) && (
          <Item
            title="Size"
            value={`${product.gender === 'Y' ? 'Youth ' : ''} ${
              product.size?.letter
                ? product.size?.letter
                : product.size?.number
                ? product.size?.number.toString()
                : ''
            }`}
          />
        )}

      {product.category2 === 'Tires' && product.size?.letter && (
        <Item
          title={`${product.size.letter1 ? 'Front ' : ''}Tire Size`}
          value={product.size.letter}
        />
      )}

      {product.category2 === 'Tires' && product.size?.letter1 && (
        <Item title="Rear Tire Size" value={product.size.letter1} />
      )}
    </div>
  );
}

const Item = ({
  title,
  value,
  linkText,
  linkHref,
  sellerNote,
}: {
  title: string;
  value?: string;
  linkText?: string;
  linkHref?: string;
  sellerNote?: string;
}) => {
  return (
    <div className="flex items-center gap-x-2">
      <span className="text-[1.8rem] font-semibold text-brand-black">
        {title}:
      </span>

      {linkHref ? (
        <Link
          href={linkHref}
          className="text-[1.8rem] text-brand-black underline"
        >
          {linkText}
        </Link>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-[1.8rem] text-brand-black">{value}</span>

          {sellerNote && (
            <InformationCircleIcon className="h-8 w-8 text-brand-secondary" />
          )}
        </div>
      )}
    </div>
  );
};
